<template>
  <ExpiredTokenView
    v-if="props.connected && userState?.status === 'expired'"
    :sign-message-params="props.signMessageParams"
  />
  <div v-else>
    <div class="wrapper">
      <AlertDetailsCard
        v-if="props.connected && selectedDetail !== undefined"
        :key="selectedDetail?.id ?? 'alert-details-card'"
        :source="selectedDetail"
        :handle-select-detail="handleSelectDetail"
        :handle-close="handleClose"
      />

      <div v-else-if="props.connected" class="notifi-dialog-header">
        <p-button
          v-if="notifiStore.fetchedCardState === 'preview'"
          class="notifi-header-button"
          icon="pi pi-arrow-left"
          @click="navigateToHistoryCard"
        />
        <h3 v-if="notifiStore.fetchedCardState === 'edit'" class="header-label">
          <label class="header-content"> {{ headerContent }} </label>
        </h3>
        <h3
          v-if="notifiStore.fetchedCardState === 'preview'"
          class="header-label"
        >
          <label class="header-content"> Manage Alerts </label>
        </h3>
        <p-button
          v-if="notifiStore.fetchedCardState === 'history'"
          class="notifi-header-button"
          icon="pi pi-cog"
          @click="navigateToPreviewCard"
        />
        <h3
          v-if="notifiStore.fetchedCardState === 'history'"
          class="header-label"
        >
          <label class="header-content"> Alert History </label>
        </h3>
        <p-button
          class="notifi-header-button"
          icon="pi pi-times"
          @click="handleClose"
        />
      </div>
      <div
        v-if="
          notifiStore.fetchedCardState === 'history' &&
          connected &&
          userState?.status === 'authenticated'
        "
        class="solid-line"
      />
    </div>
    <notifi-card
      v-if="props.connected"
      :wallet-address="props.walletAddress"
      :public-key="props.publicKey"
      :sign-message-params="props.signMessageParams"
      :environment="props.environment"
      :tenant-id="props.tenantId"
      :card-id="props.cardId"
      @on-alert-detail-selected="handleSelectDetail"
    />
  </div>

  <div class="notifi-footer-container">
    <label class="notifi-footer-label">Powered by </label>
    <a href="https://notifi.network/main" target="_blank">
      <img
        :style="{
          height: '44px',
          width: '44px',
          marginLeft: '9px',
        }"
        src="@/notifi-vue-package/assets/notifi.svg"
        class="notifi-footer"
        alt=""
      />
    </a>
  </div>
</template>
<script lang="ts" setup>
import {
  NotifiEnvironment,
  NotifiFrontendClient,
} from '@notifi-network/notifi-frontend-client';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { ExpiredTokenView, NotifiCard } from '@/notifi-vue-package/';
import { useNotifiStore } from '@/notifi-vue-package/stores';
import { Types } from '@notifi-network/notifi-graphql';
import { AlertDetailsCard } from './components';
import { connected } from 'process';

const notifiStore = useNotifiStore();
const { isFTU, userState } = storeToRefs(notifiStore);

type IProps = {
  walletAddress: string;
  publicKey: string;
  environment: NotifiEnvironment;
  tenantId: string;
  signMessageParams: Parameters<NotifiFrontendClient['logIn']>[0];
  cardId: string;
  inputs?: Record<string, string | undefined>;
  connected: boolean;
};
const props = defineProps<IProps>();

const emit = defineEmits(['onClose']);
const handleClose = () => {
  emit('onClose');
};

const selectedDetail = ref<
  Types.NotificationHistoryEntryFragmentFragment | undefined
>(undefined);

const handleSelectDetail = (
  sourceItem?: Types.NotificationHistoryEntryFragmentFragment,
) => {
  selectedDetail.value = sourceItem ?? undefined;
};

const headerContent = computed(() => {
  return isFTU.value === true
    ? 'Get alerts for all of your transaction and web3 activity'
    : 'Manage Alerts';
});

function navigateToPreviewCard() {
  notifiStore.setCardState('preview');
}
function navigateToHistoryCard() {
  notifiStore.setCardState('history');
}
</script>

<style lang="scss">
.header-content {
  display: flex;
  text-align: start;
  justify-self: center;
}

$iconColor: #d4c4ed;

.header-label {
  display: flex;
  align-items: center;
  text-align: center;
  margin-right: 6px;
}

.solid-line {
  border-top: 1px solid #e5e4fa;
  margin-top: 5px;
  opacity: 0.15;
}

.notifi-dialog-container {
  background-color: #1c1c33;
  border-radius: 24px;
  position: relative;
  top: 71px;
  right: 140px;
  @media screen and (max-width: 767px) {
    right: 0px;
  }

  filter: drop-shadow(0px 0px 18px rgba(0, 0, 0, 0.16))
    drop-shadow(0px 0px 28px rgba(0, 0, 0, 0.16))
    drop-shadow(0px 0px 52px rgba(0, 0, 0, 0.16));

  .notifi-dialog-header {
    display: flex;
    position: relative;
    z-index: 20;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 8px 24px 0px 24px;
  }
}

.notifi-dialog-content {
  width: 400px;
  border-radius: 24px;
  background-color: #1c1c33;
  padding: 0px !important;
}

.alert-history-image {
  display: flex;
}

.connect-button {
  flex-grow: 1;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  height: 54px;
  font-size: 1.1rem;
  font-weight: 500;
}

.notifi-header-button {
  background: none;
  border: none;
  width: auto !important;
  height: auto;
  color: transparentize($iconColor, 0.5) !important;

  &:hover,
  &:active,
  &:focus {
    background: none !important;
    border: none !important;
    box-shadow: none !important;
    color: transparentize($iconColor, 0.2) !important;
  }
}

.notifi-footer-container {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  left: 37%;
  bottom: 0px;
}

.notifi-next-button {
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
}

.notifi-footer-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 8px;
}

.alert-error-content {
  top: -15px;
  font-size: 12px;
  text-align: center;
  color: #c86060;
}
</style>
